/* eslint-disable react/no-array-index-key */
import {
  Grid,
  Typography,
  Button,
  InputAdornment,
  IconButton,
  MenuItem,
  Popover,
  Tooltip,
  InputBase,
  Theme,
  useMediaQuery,
  useTheme,
  styled,
  Select,
} from '@quillbot/design-system';
import { makeStyles } from '@mui/styles';
import {
  Add,
  Close,
  CloseOutlined,
  ArrowDropDown,
  Search,
  ArrowDropUp,
  ArrowBack,
} from '@mui/icons-material';
import QuillLocalStorage from 'core/utils/localStorageClient';
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@Common/models/redux/store';
import { useRouter } from 'next/router';
import { createBorder, createBorderRadius, unitPX, UNITS } from 'src/researcher/styles';
import { tab_height, tab_padding } from 'src/researcher/styles/constants';
import { LAYOUT_VALS, LAYOUT_VARS } from 'src/researcher/constants/common.constants';
import { useCitationGeneratorDispatch, useCitationGeneratorState } from '../context';
import {
  changeSearchType,
  restoreToDefaultState,
  stopSearchMode,
  toggleCitationGeneratorEditModal,
} from '../context/actionCreators';
import citationGeneratorTrack from '../handlers/track';
import { CitationSourceEnum } from '../types';
import CitaionsResultsContainer from './CitationResults';
import { CitationGeneratorXPath } from '../constants/xpath.constants';
import { getCgBreakpointDown, getCgBreakpointUp, getSearchPlaceholder } from '../utils';
import { useEditFormDispatch } from './EditForm/context';
import { initialize } from './EditForm/actionCreators';
import { useSourceSearchApis } from '../utils/sourceSearch';
import { citationSourceLabels } from '../constants/common.constants';

const containerWidthInSmallScreen = ({
  isSearchMode,
  isMobile,
}: {
  isSearchMode: boolean;
  isMobile: boolean;
}) => {
  if (isSearchMode) {
    return 'auto';
  }

  if (isMobile) {
    return '100%';
  }

  return 'calc(100% - 50px)';
};

const useStyles = makeStyles<
  Theme,
  { isSearchMode: boolean; isMobile: boolean; independentTool: boolean; isCowriter: boolean }
>((theme) => ({
  responsiveContainer: {
    boxSizing: 'border-box',
    paddingRight: '15px',
    [getCgBreakpointDown(theme)]: {
      position: (p) => (p.isSearchMode ? 'static' : 'fixed'),
      marginLeft: (p) => (p.isSearchMode ? -16 : 0),
      marginRight: (p) => (p.isSearchMode ? -4 : 0),
      width: (p) => containerWidthInSmallScreen(p),
      bottom: 0,
      left: (p) => (p.isMobile ? 0 : 50),
      zIndex: 1,
      backgroundColor: '#fff',
      padding: 8,
      border: `1px solid ${theme.specialColors.GRAY_BORDER_COLOR}`,
      borderLeft: 'none',
      borderRight: 'none',
      borderBottom: (p) => (p.isSearchMode ? 'none' : 'initial'),
    },
  },
  searchType: {
    width: '130px',
    height: '32px',
    paddingRight: '6px',
    paddingLeft: '8px',
    background: theme.palette.grey[400],
    display: 'flex',
    alignItems: 'center',
    borderRadius: '4px',
    marginRight: 8,
    justifyContent: 'space-between',
    cursor: 'pointer',
    position: 'relative',
    [getCgBreakpointDown(theme)]: {
      // width: 'unset',
      width: '98px',
      maxWidth: '98px',
      height: 'unset',
      background: 'unset',
      marginRight: 0,
    },
  },
  searchBox: {
    width: 'calc(100% - 138px)',
    height: 33,
    border: '1px solid #DEE1E3',
    borderRadius: '4px',
    display: 'flex',
    position: 'relative',
    [getCgBreakpointDown(theme)]: {
      width: 'unset',
      flex: 1,
    },
  },
  searchButton: {
    width: 65,
    height: 32,
    fontWeight: 600,
    position: 'absolute',
    right: 0,
    top: '-1px',
    borderBottomLeftRadius: 0,
    borderTopLeftRadius: 0,
  },
  dropDownSelectedText: {
    fontWeight: 600,
    fontSize: '12px',
    lineHeight: '16px',
    color: theme.palette.text.primary,
    [getCgBreakpointDown(theme)]: {
      fontSize: '10px',
    },
  },
  notchedOutline: {
    border: 'none',
  },
  inputAdornment: {
    cursor: 'pointer',
    paddingLeft: ({ isCowriter }) => (isCowriter ? '0px' : '7px'),
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
  adornedEnd: {
    paddingRight: 5,
  },
  buttonContainer: {
    borderRadius: 3,
    height: '36px',
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    top: '-1px',
  },
  toolTipText: {
    fontSize: '9px',
    color: theme.palette.text.tooltip,
    width: 70,
  },
  tooltipCloseButton: {
    padding: 1,
    height: '15px',
    marginLeft: '10px',
    color: theme.palette.text.tooltip,
  },
  tooltipPosition: {
    width: '175px',
    left: '-76px',
    height: '35px',
    margin: 0,
    bottom: '-5px',
  },
  tooltipPositionBottom: {
    bottom: '5px',
    margin: 0,
  },
  buttonStyle: {
    left: 0,
  },
  fontColor: {
    color: theme.palette.text.secondary,
  },
  compostTextContainer: {
    '&:hover': {
      '& $fontColor': {
        color: '#308140',
      },
    },
    [getCgBreakpointDown(theme)]: {
      display: 'none',
    },
  },
  previewText: {
    fontSize: '16px',
    color: theme.palette.text.secondary,
    maxWidth: 320,
    textAlign: 'center',
  },
  previewTextContainer: {
    height: '100%',
  },
  composeButton: {
    borderRadius: 50,
    paddingTop: 3,
    paddingBottom: 3,
    fontWeight: 600,
    width: '127px',
    marginBottom: 18,
    borderColor: theme.palette.primary.main,
  },
  composeButtonMobile: {
    flexDirection: 'column',
    // border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: 2,
    padding: '2px 4px',
    marginLeft: 4,
    '& > span': {
      fontSize: 12,
      fontWeight: 600,
      color: theme.palette.primary.main,
    },
    '& > .MuiSvgIcon-root': {
      height: 18,
      width: 18,
    },
    [getCgBreakpointUp(theme)]: {
      display: 'none',
    },
  },
  icon: {
    color: '#5F6368',
  },
  dropDownLabel: {
    fontSize: '11px',
    lineHeight: '12px',
    color: theme.palette.text.secondary,
    [getCgBreakpointDown(theme)]: {
      fontSize: '9px',
    },
  },
  inputPlaceHolder: {
    marginTop: '1px',
    '&::placeholder': {
      fontWeight: 'normal',
    },
  },
  backBtnWrapper: {
    display: 'none',
    [getCgBreakpointDown(theme)]: {
      display: (p) => (p.isSearchMode ? 'initial' : 'none'),
    },
  },
  backBtn: {
    padding: '5px',
    marginLeft: '15px',
    marginTop: '5px',
    '& > .btnText': {
      display: 'none',
      fontSize: 12,
    },
    [getCgBreakpointDown(theme)]: {
      borderRadius: 50,
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      paddingRight: 22,
      marginLeft: 0,
      marginBottom: 12,
      '& > .btnText': {
        display: 'initial',
      },
      '& > .btnArrow': {
        marginRight: 8,
        fontSize: 14,
      },
    },
  },
  composeTextButtonAndResultContainer: {
    height: 'calc(100% - 36px - 48px - 12px)',
    [getCgBreakpointDown(theme)]: {
      height: 'calc(100vh - 156px)',
      display: (p) => (p.isSearchMode ? 'flex' : 'none'),
    },
  },
}));

type ComposeTextButtonType = {
  classes: ReturnType<typeof useStyles>;
  sortBy: string;
  onSortChange: Function;
  showSortBy: boolean;
  isSearchMode: boolean;
};

// eslint-disable-next-line @typescript-eslint/naming-convention
const total_region = `calc(${tab_height} - ${LAYOUT_VALS.drawerMenubarHeight})`;

// top-bottom paddings to region
// eslint-disable-next-line @typescript-eslint/naming-convention
const view_region = `calc(${total_region} - ${unitPX(tab_padding * 2)})`;

// eslint-disable-next-line @typescript-eslint/naming-convention
const anchor_height = UNITS['5pt'];

// view region - back button area
// eslint-disable-next-line @typescript-eslint/naming-convention
const net_scroll_height = `calc(${view_region} - ${unitPX(UNITS['4pt'])})`;

const SortByWrapper = styled(Grid, {
  shouldForwardProp: (prop) => prop !== 'show',
})<{ show: boolean }>`
  display: ${({ show }) => (show ? 'flex' : 'none')};
  align-items: center;
  padding-top: 4px;
  padding-bottom: 4px;

  ${({ theme }) => getCgBreakpointDown(theme)} {
    flex: 1;
    justify-content: center;
    padding-right: 8px;
  }
`;
const SortBy = styled(Select)`
  margin-left: 8px;
  font-size: 13px;

  .MuiSelect-select {
    padding-top: 6px;
    padding-bottom: 6px;
  }

  ${({ theme }) => getCgBreakpointDown(theme)} {
    max-width: 100vw;
    width: 100%;
  }
`;
const SortMenuItem = styled(MenuItem)`
  font-size: 13px;
  min-height: unset;
`;

const StyledGrid = styled(Grid)<{ isSearchMode: boolean; isHidden?: boolean }>(
  ({ theme, isSearchMode, isHidden }) => ({
    paddingRight: '15px',
    marginBottom: '8px',
    marginTop: '4px',
    visibility: isHidden ? 'hidden' : 'visible',
    [getCgBreakpointDown(theme)]: {
      paddingRight: 0,
      marginTop: isSearchMode ? '-4px' : undefined,
      marginBottom: isSearchMode ? '8px' : undefined,
    },
  })
);

const ComposeTextButtonAndResultContainer = (props: ComposeTextButtonType) => {
  const { classes } = props;
  const router = useRouter();
  const isCowriter = router.pathname.includes('/co-writer');
  const citationGeneratorDispatch = useCitationGeneratorDispatch();
  const editFormDispatch = useEditFormDispatch();
  const citations = useCitationGeneratorState((state) => state.citations);
  const isLoadingResults = useCitationGeneratorState((state) => state.isLoadingResults);
  const independentTool = useSelector((state: RootState) => state.common.independentTool);

  return (
    <Grid
      data-id
      container
      className={classes.composeTextButtonAndResultContainer}
      style={{
        maxHeight: isCowriter ? `${net_scroll_height} !important` : undefined,
        paddingBottom: isCowriter ? unitPX(tab_padding) : undefined,
        flexGrow: isCowriter ? 1 : undefined,
      }}
    >
      {citations.length || isLoadingResults ? (
        <>
          <StyledGrid
            container
            item
            justifyContent="space-between"
            alignItems="center"
            height="34px"
            isHidden={!citations.length && isLoadingResults}
            isSearchMode={props.isSearchMode}
          >
            <Tooltip
              classes={{
                tooltipPlacementTop: `${classes.tooltipPositionBottom} ${classes.buttonStyle}`,
                tooltipPlacementBottom: `${classes.tooltipPosition} ${classes.buttonStyle}`,
              }}
              title="Manually enter citation details"
              placement="top"
              arrow
            >
              <Grid
                onClick={() => {
                  citationGeneratorDispatch(toggleCitationGeneratorEditModal(true));
                  editFormDispatch(initialize());
                  citationGeneratorTrack.userClickedComposeCitation();
                }}
                container
                className={classes.compostTextContainer}
                item
                style={{
                  // height: '34px',
                  padding: '6px 0px',
                  cursor: 'pointer',
                  width: '80px',
                  // visibility: !citations.length && isLoadingResults ? 'hidden' : 'visible',
                }}
                alignItems="center"
              >
                <Grid item>
                  <Add fontSize="small" className={classes.fontColor} />
                </Grid>

                <Grid item>
                  <Typography
                    className={classes.fontColor}
                    style={{
                      height: '22px',
                      fontSize: '13px',
                      fontWeight: 700,
                      marginTop: '-2px',
                    }}
                  >
                    Compose
                  </Typography>
                </Grid>
              </Grid>
            </Tooltip>

            <SortByWrapper show={props.showSortBy && citations.length !== 1}>
              <div style={{ fontSize: '13px', whiteSpace: 'nowrap' }}>Sort by:</div>

              <SortBy
                value={props.sortBy}
                variant="outlined"
                onChange={(e) => {
                  props.onSortChange(e.target.value as string);
                }}
                MenuProps={{
                  PaperProps: {
                    elevation: 4,
                  },
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'right',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                  },
                }}
              >
                <SortMenuItem value="relevance">Most Relevant</SortMenuItem>
                <SortMenuItem value="is-referenced-by-count">Most Cited</SortMenuItem>
                <SortMenuItem value="published">Newest</SortMenuItem>
                <SortMenuItem value="updated">Recently Updated</SortMenuItem>
              </SortBy>
            </SortByWrapper>
          </StyledGrid>

          <Grid
            item
            container
            style={{
              paddingBottom: '10px',
              // flex: independentTool ? 1 : undefined,
              ...(isCowriter
                ? {
                    height: 'calc(100% - 34px)',
                  }
                : null),
            }}
          >
            <CitaionsResultsContainer
              net_scroll_cw_height={`calc(${net_scroll_height} - ${unitPX(UNITS['4pt'])})`}
            />
          </Grid>
        </>
      ) : (
        <Grid
          {...CitationGeneratorXPath.ComposeCitation}
          container
          style={{
            // height: `calc(100% - ${independentTool ? '84px' : '75px'})`,
            height: independentTool ? undefined : 'calc(100% - 75px)',
            ...(isCowriter
              ? {
                  height: 'calc(100%)',
                }
              : null),
          }}
          alignItems="center"
          justifyContent="center"
        >
          <Grid item container justifyContent="center">
            <Button
              {...CitationGeneratorXPath.ComposeCitation}
              startIcon={<Add />}
              onClick={() => {
                citationGeneratorDispatch(toggleCitationGeneratorEditModal(true));
                editFormDispatch(initialize());
                citationGeneratorTrack.userClickedComposeCitation();
              }}
              variant="outlined"
              color="primary"
              className={classes.composeButton}
            >
              Compose
            </Button>

            <Grid
              item
              container
              className={classes.previewTextContainer}
              justifyContent="center"
              alignItems="center"
            >
              <Typography className={classes.previewText}>
                Cite a wide range of sources and export them in your favorite styles!
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

export default function ToolBarAndResultContainer() {
  const router = useRouter();
  const isCowriter = router.pathname.includes('/co-writer');

  const isMobile = useSelector((rootState: RootState) => rootState.common.platform.isMobile);
  const isSearchMode = useCitationGeneratorState((state) => state.isSearchMode);
  const independentTool = useSelector((rootState: RootState) => rootState.common.independentTool);
  const classes = useStyles({
    isSearchMode,
    isMobile,
    independentTool,
    isCowriter,
  });
  const [showSearchToolTip, setShowSearchToolTip] = useState(false);
  const isEditFormOpen = useCitationGeneratorState((state) => state.isEditFormOpen);
  const citationGeneratorDispatch = useCitationGeneratorDispatch();
  const editFormDispatch = useEditFormDispatch();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(getCgBreakpointDown(theme));

  const { query, setQuery, search, sortBy, setSortBy } = useSourceSearchApis({
    onSearch: () => {
      if (isSmallScreen) {
        window.scrollTo(0, 0);
      }
    },
  });

  useEffect(() => {
    if (QuillLocalStorage.getInstance().get('isCitationSearchTooltipShown') !== 'true') {
      setShowSearchToolTip(true);
      QuillLocalStorage.getInstance().set('isCitationSearchTooltipShown', 'true');
      setTimeout(() => {
        setShowSearchToolTip(false);
        // setShowButtonToolTip(true);
        // setTimeout(() => {
        //   setShowButtonToolTip(false);
        // }, 5000);
      }, 5000);
    }
    return () => {
      citationGeneratorDispatch(restoreToDefaultState());
    };
  }, []);

  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

  const searchType = useCitationGeneratorState((state) => state.searchType);

  /*  const isLoading = status === AsyncStatusEnum.PENDING;
  const isSuccess = status === AsyncStatusEnum.RESOLVE; */
  // make api request as debounce search value changes

  if (isSmallScreen && isEditFormOpen) {
    return null;
  }

  return (
    <>
      <Grid container className={classes.backBtnWrapper}>
        <IconButton
          size="large"
          onClick={() => {
            citationGeneratorDispatch(stopSearchMode());
          }}
          className={classes.backBtn}
        >
          <ArrowBack className="btnArrow" />

          <span className="btnText">Back to citation list</span>
        </IconButton>
      </Grid>

      <Grid
        container
        justifyContent="space-between"
        alignItems="center"
        className={classes.responsiveContainer}
      >
        <Popover
          {...CitationGeneratorXPath.CitationSourceSelectPopover}
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          elevation={4}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          {Object.values(CitationSourceEnum).map((source) => (
            <MenuItem
              style={{
                fontSize: '13px',
                width:
                  !isSmallScreen && anchorEl ? window.getComputedStyle(anchorEl).width : undefined,
                backgroundColor: source === searchType ? '#f1f1f1' : undefined,
              }}
              key={source}
              disabled={source === searchType}
              onClick={() => {
                citationGeneratorDispatch(changeSearchType(source));
                // citationGeneratorTrack.changedCitationSource(
                //   TriggeringActions.SEARCH_RESULT,
                //   source
                // );
                setAnchorEl(null);
              }}
            >
              {citationSourceLabels[source].label}
            </MenuItem>
          ))}
        </Popover>

        <Grid
          item
          container
          className={classes.buttonContainer}
          sx={[isCowriter && { mt: unitPX(UNITS.pt) }]}
        >
          <Grid
            {...CitationGeneratorXPath.CitationSourceSelect}
            item
            className={classes.searchType}
            onClick={(e) => {
              setAnchorEl(e.currentTarget as HTMLDivElement);
            }}
          >
            <Grid item>
              <Typography className={classes.dropDownLabel}>Citation Source</Typography>
              <Typography className={classes.dropDownSelectedText}>
                {citationSourceLabels[searchType].label}
              </Typography>
            </Grid>

            {isSmallScreen ? (
              <ArrowDropUp
                style={{
                  color: theme.specialColors.ICON_DEFAULT_COLOR,
                  position: 'absolute',
                  right: 0,
                  top: 8,
                }}
              />
            ) : (
              <ArrowDropDown style={{ color: theme.specialColors.ICON_DEFAULT_COLOR }} />
            )}
          </Grid>

          <Tooltip
            classes={{
              tooltipPlacementTop: classes.tooltipPosition,
              tooltipPlacementBottom: `${classes.tooltipPosition} ${classes.tooltipPositionBottom}`,
            }}
            title={
              <Grid container justifyContent="space-between">
                <Typography className={classes.toolTipText}>
                  Start with a URL example.com
                </Typography>
                <IconButton
                  className={classes.tooltipCloseButton}
                  onClick={() => {
                    setShowSearchToolTip(false);
                  }}
                  size="large"
                >
                  <CloseOutlined style={{ fontSize: '15px' }} />
                </IconButton>
              </Grid>
            }
            placement="top"
            open={showSearchToolTip}
            arrow
          >
            <Grid
              item
              className={classes.searchBox}
              sx={[isCowriter && { border: 'none !important' }]}
              style={{
                paddingLeft: (() => {
                  if (isCowriter) return 0;
                  if (isSmallScreen || searchType === CitationSourceEnum.WEBSITE) return 8;
                  return undefined;
                })(),
              }}
            >
              <InputBase
                sx={[
                  isCowriter &&
                    createBorder(['top', 'right', 'bottom', 'left'], {
                      color: (t: Theme) => t.palette.divider,
                    }),
                  isCowriter &&
                    createBorderRadius(
                      ['topLeft', 'topRight', 'bottomRight', 'bottomLeft'],
                      unitPX(UNITS['0.5pt'])
                    ),
                  isCowriter && {
                    py: unitPX(UNITS['0.5pt']),
                    px: unitPX(UNITS.pt),
                  },
                ]}
                value={query}
                autoComplete="off"
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  width: 'calc(100% - 70px)',
                }}
                inputProps={{
                  style: {
                    paddingTop: '5px',
                  },
                  className: classes.inputPlaceHolder,
                }}
                placeholder={getSearchPlaceholder(searchType)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setQuery(e.target.value);
                }}
                onKeyUp={(e: React.KeyboardEvent) => {
                  if (e.key === 'Enter') {
                    search();
                    if (e.target) (e.target as HTMLInputElement)?.blur?.();
                  }
                }}
                id="outlined-adornment-amount"
                startAdornment={
                  isSmallScreen || searchType === CitationSourceEnum.WEBSITE ? null : (
                    <InputAdornment
                      position="start"
                      className={classes.inputAdornment}
                      onClick={search}
                    >
                      <Search className={classes.icon} />
                    </InputAdornment>
                  )
                }
                endAdornment={
                  query ? (
                    <InputAdornment position="end" className={classes.inputAdornment}>
                      <IconButton
                        onClick={() => {
                          setQuery('');
                        }}
                        style={{
                          padding: 1,
                        }}
                        size="large"
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null
                }
              />

              <Button
                variant="contained"
                color="primary"
                className={classes.searchButton}
                onClick={search}
                sx={[
                  isCowriter && {
                    ml: unitPX(UNITS.pt),
                    borderRadius: `${unitPX(UNITS['2pt'])} !important`,
                  },
                ]}
              >
                <span style={{ lineHeight: 1 }}>
                  {searchType === CitationSourceEnum.WEBSITE ? 'Cite' : 'Search'}
                </span>
              </Button>
            </Grid>
          </Tooltip>

          {/* Below component shows up only on small screens */}
          <IconButton
            size="small"
            className={classes.composeButtonMobile}
            onClick={() => {
              citationGeneratorDispatch(toggleCitationGeneratorEditModal(true));
              editFormDispatch(initialize());
              citationGeneratorTrack.userClickedComposeCitation();
            }}
          >
            <Add color="primary" />
            <span>Compose</span>
          </IconButton>
        </Grid>
      </Grid>

      <ComposeTextButtonAndResultContainer
        sortBy={sortBy}
        onSortChange={setSortBy}
        classes={classes}
        showSortBy={searchType === CitationSourceEnum.JOURNAL}
        isSearchMode={isSearchMode}
      />
    </>
  );
}
